import { type useQuery } from '@apollo/client/react';
import { createContext, useContext } from 'react';

import { type MyAccessQuery, type PermissionActionEnum, type PermissionEntityEnum } from '@/graphql/generated/graphql';

// Actions/entities are the raw registry strings; the generated enums share
// those values, so both are accepted at call sites.
export type AccessAction = PermissionActionEnum | string;
export type AccessEntity = PermissionEntityEnum | string;

interface AccessContextValueType {
  /** Effective grants of the current user. projectIds null = company-wide. */
  grants: MyAccessQuery['myAccess'] | undefined;
  loadingPermissions: boolean;
  loadingRequests: boolean;
  refetchPermissions: ReturnType<typeof useQuery<MyAccessQuery>>['refetch'];
  /**
   * Precise check. Without projectId it asks "company-wide?"; pass projectId
   * for project-contextual gates. For record-level decisions prefer the
   * record's `abilities` field from GraphQL — the backend is the authority.
   */
  can: (action: AccessAction, entity: AccessEntity, opts?: { projectId?: string }) => boolean;
  /** Held anywhere at all (company-wide or any project) — page/nav gating. */
  canAnywhere: (action: AccessAction, entity: AccessEntity) => boolean;
  /** Legacy alias of canAnywhere, kept for existing call sites. */
  canPerformAction: (action: AccessAction, entity: AccessEntity | AccessEntity[], condition?: 'or' | 'and') => boolean;
  requestPermission: (action: AccessAction, entity: AccessEntity, projectId?: string) => void;
  checkRequestExists: (action: AccessAction, entity: AccessEntity) => boolean;
}

const PermissionContext = createContext<AccessContextValueType>({
  grants: undefined,
  loadingPermissions: true,
  loadingRequests: true,
  refetchPermissions: Promise.resolve({}) as unknown as AccessContextValueType['refetchPermissions'],
  can: () => false,
  canAnywhere: () => false,
  canPerformAction: () => false,
  requestPermission: () => {
    throw new Error('requestPermission called outside of PermissionsContextProvider');
  },
  checkRequestExists: () => false,
});

export const useAccess = () => useContext(PermissionContext);

export default PermissionContext;
